import type { AppLocale } from "@/i18n/locales";

import type { ThreatReportSummary } from "./cors-edge-lambda-api";
import {
  reportLanguageMatchesLocale,
  responseLanguageOf,
  threatReportForLocale,
} from "./threat-report-language";

export type ReportIdentity = { chatKey: string; epochTimeSecs: number };

/** The completed report whose body is on screen, recorded when that body is set. */
export type LoadedThreatReport = ReportIdentity & {
  caseId: string;
  responseLanguage: string | null;
};

export type LanguageSwitchInput = {
  locale: AppLocale;
  caseId: string | undefined;
  /** False while a report is generating or the viewer cannot open another version. */
  enabled: boolean;
  /** Error and not-found screens: nothing to align and no cover to show. */
  blocked: boolean;
  loaded: LoadedThreatReport | null;
};

export type LanguageSwitchState = {
  /** Body already checked for the current case and language. Each body is checked once. */
  checked: string | null;
  lookup: { id: number; context: string; from: ReportIdentity } | null;
  /** Report the switch opened. The cover stays until that body replaces `from`. */
  awaiting: { context: string; from: ReportIdentity; target: ReportIdentity } | null;
  /** Version the analyst picked. Left alone until the case or language changes. */
  userPick: { context: string; report: ReportIdentity } | null;
  /** Case and language that have no completed report to open. */
  prompt: string | null;
  lastLookupId: number;
};

export const initialLanguageSwitchState: LanguageSwitchState = {
  checked: null,
  lookup: null,
  awaiting: null,
  userPick: null,
  prompt: null,
  lastLookupId: 0,
};

export function loadedThreatReportOf(
  row: Record<string, unknown> | null | undefined,
  caseId: string | null | undefined,
): LoadedThreatReport | null {
  const trimmedCaseId = caseId?.trim();
  const chatKey = row?.chat_key;
  const epochTimeSecs = Number(row?.epoch_time_secs);
  if (!trimmedCaseId || typeof chatKey !== "string" || !chatKey || !Number.isFinite(epochTimeSecs)) {
    return null;
  }
  return { caseId: trimmedCaseId, chatKey, epochTimeSecs, responseLanguage: responseLanguageOf(row) };
}

function sameReport(left: ReportIdentity, right: ReportIdentity): boolean {
  return left.chatKey === right.chatKey && Number(left.epochTimeSecs) === Number(right.epochTimeSecs);
}

function identityOf(report: ReportIdentity): ReportIdentity {
  return { chatKey: report.chatKey, epochTimeSecs: Number(report.epochTimeSecs) };
}

function contextOf(input: LanguageSwitchInput): string {
  return `${input.caseId ?? ""}\u0000${input.locale}`;
}

function checkedKeyFor(context: string, report: ReportIdentity): string {
  return `${context}\u0000${report.chatKey}\u0000${Number(report.epochTimeSecs)}`;
}

/** A body left over from the previous case is not the open report for this one. */
function loadedForCase(input: LanguageSwitchInput): LoadedThreatReport | null {
  return input.loaded && input.caseId && input.loaded.caseId === input.caseId ? input.loaded : null;
}

/** The analyst opened ``report`` from the version picker. */
export function recordLanguageSwitchPick(
  state: LanguageSwitchState,
  input: LanguageSwitchInput,
  report: ReportIdentity,
): LanguageSwitchState {
  const context = contextOf(input);
  const loaded = loadedForCase(input);
  return {
    ...state,
    userPick: { context, report: identityOf(report) },
    lookup: null,
    awaiting: null,
    prompt: null,
    // The body still on screen is settled: a lookup must not replace the pick.
    checked: loaded ? checkedKeyFor(context, loaded) : state.checked,
  };
}

/**
 * The list fetch for ``lookupId`` will not finish. A pick keeps the body
 * settled. Anything else may look the body up again, which is what a restarted
 * effect (Strict Mode, or enabled/blocked changing mid-fetch) has to do.
 */
export function abandonLanguageSwitchLookup(
  state: LanguageSwitchState,
  lookupId: number,
): LanguageSwitchState {
  const lookup = state.lookup;
  if (!lookup || lookup.id !== lookupId) return state;
  const keepChecked = state.userPick != null && state.userPick.context === lookup.context;
  const owned = checkedKeyFor(lookup.context, lookup.from);
  return {
    ...state,
    lookup: null,
    checked: !keepChecked && state.checked === owned ? null : state.checked,
  };
}

/**
 * Check the body on screen against the UI language. Returns ``lookupId`` when
 * the caller must list the case's reports and pass them to
 * {@link finishLanguageSwitchLookup}.
 */
export function evaluateLanguageSwitch(
  state: LanguageSwitchState,
  input: LanguageSwitchInput,
): { state: LanguageSwitchState; lookupId?: number } {
  const context = contextOf(input);
  let next = state;
  const update = (patch: Partial<LanguageSwitchState>) => {
    next = { ...next, ...patch };
  };

  if (next.lookup && next.lookup.context !== context) update({ lookup: null });
  if (next.awaiting && next.awaiting.context !== context) update({ awaiting: null });
  if (next.userPick && next.userPick.context !== context) update({ userPick: null });
  if (next.prompt != null && next.prompt !== context) update({ prompt: null });

  if (input.blocked) {
    if (next.lookup || next.awaiting) update({ lookup: null, awaiting: null });
    return { state: next };
  }

  const loaded = loadedForCase(input);
  const checkedKey = loaded ? checkedKeyFor(context, loaded) : null;

  // A lookup belongs to the body it started from. Delete, revert, generate,
  // or a case change replace that body, and the result no longer applies.
  if (next.lookup && !(loaded && sameReport(loaded, next.lookup.from))) update({ lookup: null });
  if (next.awaiting) {
    if (loaded && sameReport(loaded, next.awaiting.target)) {
      // The opened report is on screen. It is not checked again, so a row
      // that disagrees with its version list entry cannot loop.
      update({ awaiting: null, checked: checkedKey });
      return { state: next };
    }
    if (!(loaded && sameReport(loaded, next.awaiting.from))) update({ awaiting: null });
  }

  if (!input.enabled || !loaded || next.checked === checkedKey) return { state: next };
  update({ checked: checkedKey });
  if (next.lookup || next.awaiting) return { state: next };
  if (next.userPick && sameReport(next.userPick.report, loaded)) return { state: next };
  if (reportLanguageMatchesLocale(loaded.responseLanguage, input.locale)) {
    if (next.prompt != null) update({ prompt: null });
    return { state: next };
  }

  const id = next.lastLookupId + 1;
  update({ lastLookupId: id, lookup: { id, context, from: identityOf(loaded) }, prompt: null });
  return { state: next, lookupId: id };
}

/**
 * ``reports`` is the case's version list, or null when it could not be loaded.
 * Returns ``open`` when the caller must open that version.
 */
export function finishLanguageSwitchLookup(
  state: LanguageSwitchState,
  input: LanguageSwitchInput,
  lookupId: number,
  reports: readonly ThreatReportSummary[] | null,
): { state: LanguageSwitchState; open?: ThreatReportSummary } {
  const lookup = state.lookup;
  if (!lookup || lookup.id !== lookupId) return { state };
  const context = contextOf(input);
  const loaded = loadedForCase(input);
  const dropped =
    !input.enabled ||
    input.blocked ||
    lookup.context !== context ||
    !loaded ||
    !sameReport(loaded, lookup.from) ||
    (state.userPick != null && state.userPick.context === lookup.context);
  if (dropped) return { state: abandonLanguageSwitchLookup(state, lookupId) };
  const next: LanguageSwitchState = { ...state, lookup: null };
  // A failed list is not evidence that no report exists in this language.
  if (reports == null) return { state: next };
  const match = threatReportForLocale(reports, input.locale);
  if (!match) return { state: { ...next, prompt: context } };
  if (sameReport(match, loaded)) return { state: next };
  return {
    state: { ...next, awaiting: { context, from: lookup.from, target: identityOf(match) } },
    open: match,
  };
}

export function languageSwitchView(
  state: LanguageSwitchState,
  input: LanguageSwitchInput,
): { promptOpen: boolean; isSwitching: boolean } {
  const context = contextOf(input);
  if (input.blocked || !input.enabled) return { promptOpen: false, isSwitching: false };
  return {
    promptOpen: state.prompt === context,
    isSwitching: state.lookup?.context === context || state.awaiting?.context === context,
  };
}
